import PropTypes from 'prop-types';
import { makeStyles, FormHelperText } from '@material-ui/core';
import { memo, useCallback } from 'react';
import CustomButton from '../customButton';
import { reorder, replaceObjectInList } from '../../../utility/helpers';
import RecruitmentStepsField from './recruitmentStepsField';
import ReorderItems from '../reorderItems';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  content: {
    width: '100%',
  },
  header: {
    display: 'grid',
    gridTemplateColumns: 'minMax(0, 106px) minMax(0, 1fr)',
    gridColumnGap: 8,
  },
  firstItem: {
    paddingLeft: spacing(9.5),
  },
  stepWrapper: {
    cursor: 'pointer',
    boxSizing: 'border-box',
    border: `2px solid ${primary.bluish7}`,
    borderRadius: 15.76,
    padding: spacing(4),
    height: 96,
    width: 101,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  selectedStep: {
    border: `2px solid ${primary.blue1}`,
  },
  label: {
    lineHeight: '18px',
  },
  disabledSelect: {
    cursor: 'not-allowed',
  },
  anddNewButton: {
    width: '100%',
  },
}));

const RecruitmentStepsFields = ({
  translations,
  options,
  steps = [],
  initialSteps,
  fieldTypes,
  error = null,
  onChange,
}) => {
  const classes = useStyles();

  const handleAddField = useCallback(() => {
    return onChange([...steps, { ...initialSteps }]);
  }, [steps, initialSteps, onChange]);

  const handleDeleteField = useCallback(
    fieldIndex => () => {
      const updatedFields = steps.filter(
        (field, index) => index !== fieldIndex
      );

      return onChange(updatedFields);
    },
    [steps, onChange]
  );

  const handleChangeField = useCallback(
    (field, fieldIndex) => {
      onChange(replaceObjectInList(steps, fieldIndex, field));
    },
    [steps, onChange]
  );

  const handleReorder = result => {
    if (!result.destination || result.source.index === result.destination.index)
      return;

    const updatedOrder = reorder(
      steps,
      result.source.index,
      result.destination.index
    );

    onChange(updatedOrder);
  };

  return (
    <div className={classes.content}>
      <div className={classes.header}>
        <FormHelperText
          classes={{
            root: classes.firstItem,
          }}
        >
          {translations.headers.chooseType}
        </FormHelperText>
        <FormHelperText
          classes={{
            root: classes.headerItem,
          }}
        >
          {translations.headers.title}
        </FormHelperText>
      </div>
      <ReorderItems onSave={handleReorder}>
        {steps.map((step, index) => (
          <RecruitmentStepsField
            key={`field_item_${index}`}
            id={index}
            translations={translations}
            steps={steps}
            options={options}
            field={step}
            fieldTypes={fieldTypes}
            error={error?.[index]}
            onDelete={handleDeleteField(index)}
            onChange={handleChangeField}
          />
        ))}
      </ReorderItems>
      <div className={classes.anddNewButton}>
        <CustomButton
          className={classes.anddNewButton}
          type="addWithTextDarkSlimNew"
          onClick={handleAddField}
        >
          {translations.addFieldButton}
        </CustomButton>
      </div>
    </div>
  );
};

RecruitmentStepsFields.propTypes = {
  translations: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  fieldTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  initialSteps: PropTypes.object.isRequired,
  steps: PropTypes.arrayOf(PropTypes.object),
  error: PropTypes.object,
};

export default memo(RecruitmentStepsFields);
